import { SubjectsSortByQueryParams } from "@hooks/use-subjects-sort-by-query";
import { QueryParams } from "@router/route-params";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

export interface UseSortingReturn<T extends string> {
  /** Current sort criteria from URL or default */
  sortBy: T;

  /** Function to update the sort criteria in URL */
  setSortBy: (newSortBy: T) => void;
}

/**
 * Generic hook for managing a sorting parameter in the URL
 */
export function useSortingQueryParam<T extends string>({
  defaultSort,
  isValidSort,
}: SubjectsSortByQueryParams<T>): UseSortingReturn<T> {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasReadSortQueryParam = useRef<boolean>(false);

  // Get sort value from URL or use default
  const newSortBy = useMemo(() => {
    const urlSortBy = searchParams.get(QueryParams.sortBy);
    // Validate that the query parameter is a valid sort option using type guard
    if (isValidSort(urlSortBy)) {
      return urlSortBy;
    }
    
    // Default to provided default sort if no valid sort parameter
    return defaultSort;
  }, [searchParams, defaultSort, isValidSort]);
  
    const [sortBy, setSortBy] = useState<T>(newSortBy);

    // Function to update sort in query parameters
    const setNewSortBy = useCallback((newSortBy: T) => {
      
    const newSearchParams = new URLSearchParams(searchParams.toString());
    newSearchParams.set(QueryParams.sortBy, newSortBy);

    // Replace the current history entry to avoid cluttering history
    // eslint-disable-next-line @typescript-eslint/naming-convention -- name given by react-router package
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Read the sort query param only once when the component is mounted
  useEffect(() => {
    if (!hasReadSortQueryParam.current) {
      hasReadSortQueryParam.current = true;
      setSortBy(newSortBy);
    }
  }, [newSortBy]);

  // Update the URL query parameter when sortBy state changes
  useEffect(() => {
    if (!hasReadSortQueryParam.current) {
      return;
    }

    if (sortBy !== newSortBy) {
      setNewSortBy(sortBy);
    }
  }, [sortBy, newSortBy, setNewSortBy]);
  
  return {
    sortBy,
    setSortBy,
  };
}
